// BUILD-042 — Edit form save/retry sequencing (pure, dependency-injected).
//
// This is the ACTUAL logic the Form Library "Edit" dialog (DocumentTemplatesPanel's EditFormModal)
// runs for a title/description edit optionally followed by a file replacement. It is factored out
// of the component so the exact sequencing, partial-save handling and upload-failure classification
// can be exercised directly (no DOM) while remaining the one code path the component uses.
//
// Correctness properties this encodes:
//  1. Metadata is saved first; its persisted (API-returned) values become the new baseline so a
//     RETRY never re-POSTs metadata that already saved.
//  2. A file-upload failure is classified:
//       - 'retryable' — a definite non-create (pre-send client error, or an HTTP 4xx received and
//         rejected before any version was written). Safe to re-upload.
//       - 'uncertain' — outcome unknown (no HTTP response at all: network/timeout; or an HTTP 5xx
//         after the request reached the server). A version MAY exist. We must not claim "nothing
//         uploaded" and must not blindly create another version.
//  3. On retry, metadata is skipped when the baseline already matches (no duplicate metadata write);
//     the file is only (re)created on a 'retryable' prior failure, never on an 'uncertain' one.

export interface EditTemplateApi {
  updateMetadata: (input: { name: string; description: string }) => Promise<{ name: string; description: string | null }>;
  encodeFile: (file: File) => Promise<string>;
  createVersion: (input: { fileName: string; fileContentBase64: string; contentType: string }) => Promise<unknown>;
}

export interface EditTemplateInput {
  name: string;
  description: string;
  file: File | null;
  /** Current persisted baseline (what the server already holds). Metadata is sent only if this differs. */
  baseline: { name: string; description: string | null };
}

export type EditSaveOutcome =
  | { kind: 'noop' }
  | { kind: 'success'; metadataSaved: boolean; versionCreated: boolean; baseline: { name: string; description: string | null } }
  | { kind: 'metadata-error'; message: string }
  | { kind: 'file-retryable'; metadataSaved: boolean; baseline: { name: string; description: string | null }; message: string }
  | { kind: 'file-uncertain'; metadataSaved: boolean; baseline: { name: string; description: string | null }; message: string };

function metadataChanged(input: EditTemplateInput): boolean {
  return input.name.trim() !== input.baseline.name || (input.description.trim() || null) !== input.baseline.description;
}

/** Classify a thrown createVersion error. HTTP 4xx (status present, < 500) == definite non-create
 *  (received and rejected before a write). Anything else (no status: fetch rejected / network /
 *  timeout; or 5xx) == uncertain. */
export function classifyUploadFailure(e: any): 'retryable' | 'uncertain' {
  const status = e && typeof e.status === 'number' ? e.status : undefined;
  if (status !== undefined && status >= 400 && status < 500) return 'retryable';
  return 'uncertain';
}

export async function runEditTemplateSave(api: EditTemplateApi, input: EditTemplateInput): Promise<EditSaveOutcome> {
  const wantsMetadata = metadataChanged(input);
  if (!wantsMetadata && !input.file) return { kind: 'noop' };

  let baseline = input.baseline;
  let metadataSaved = false;

  if (wantsMetadata) {
    try {
      const updated = await api.updateMetadata({ name: input.name.trim(), description: input.description.trim() });
      metadataSaved = true;
      // Advance baseline to the server truth so a retry does NOT resend metadata.
      baseline = { name: updated.name, description: updated.description ?? null };
    } catch (e: any) {
      return { kind: 'metadata-error', message: e?.message || 'Failed to save changes.' };
    }
  }

  if (input.file) {
    // Pre-send encode: a failure here means nothing was sent → definite non-create → retryable.
    let encoded: string;
    try {
      encoded = await api.encodeFile(input.file);
    } catch {
      return { kind: 'file-retryable', metadataSaved, baseline, message: 'Could not read the selected file, so nothing was uploaded.' };
    }
    try {
      await api.createVersion({ fileName: input.file.name, fileContentBase64: encoded, contentType: input.file.type || 'application/pdf' });
    } catch (e: any) {
      const cls = classifyUploadFailure(e);
      if (cls === 'retryable') {
        return { kind: 'file-retryable', metadataSaved, baseline, message: e?.message || 'The file upload was rejected.' };
      }
      return { kind: 'file-uncertain', metadataSaved, baseline, message: e?.message || 'The file upload result is uncertain.' };
    }
    return { kind: 'success', metadataSaved, versionCreated: true, baseline };
  }

  return { kind: 'success', metadataSaved, versionCreated: false, baseline };
}
